import Handlebars from 'handlebars';

/**
 * JS ports of the Handlebars helpers templates-service renders with, so the editor preview matches
 * the PDF the backend produces.
 *
 * Source of truth:
 *   java-service-common/common-template/src/main/java/zervio/templating/TemplatingService.java
 *   java-service-common/common-template/src/main/java/zervio/templating/helpers/*.java
 *
 * If a helper is added, changed or removed there, mirror it here - the two sets are independent and
 * will otherwise drift. Anything not listed in ZERVIO_HELPERS below is NOT available server-side.
 *
 * All 20 jknack StringHelpers are ported below, from the handlebars 4.5.0 sources (the version
 * templates-service resolves). Two documented approximations: numberFormat's DecimalFormat pattern
 * support covers grouping / fraction-digit / percent patterns rather than the whole grammar, and
 * stringFormat covers the common %s %d %f conversions rather than all of java.util.Formatter.
 */

const DEFAULT_PATTERN = 'YYYY-MM-dd HH:mm:ss';
const DEFAULT_LOCALE = 'en-GB';

const toLocale = locale => (locale ? String(locale).replace(/_/g, '-') : DEFAULT_LOCALE);

const toNumber = value => {
  if (value instanceof JavaDouble) return value.value;
  if (typeof value === 'number') return value;
  if (value === null || value === undefined || value === '') return 0;
  const parsed = Number(value);
  return Number.isNaN(parsed) ? 0 : parsed;
};

/**
 * The zervio arithmetic helpers return a Java double, and handlebars renders it with
 * Double.toString - so an integral result carries a trailing ".0": {{sumList items "amount"}}
 * prints "350.0" on the server, not "350". This carries that rendering while still behaving as a
 * number wherever the value is fed into another helper.
 */
class JavaDouble {
  constructor(value) {
    this.value = value;
  }

  valueOf() {
    return this.value;
  }

  toString() {
    return Number.isInteger(this.value) ? `${this.value}.0` : String(this.value);
  }

  /**
   * Without this, `'' + value` - which is how handlebars stringifies a non-string - takes the
   * default hint, prefers valueOf, and prints "350" instead of "350.0".
   */
  [Symbol.toPrimitive](hint) {
    return hint === 'number' ? this.value : this.toString();
  }

  toJSON() {
    return this.value;
  }
}

const javaDouble = value => new JavaDouble(value);

const isNumeric = value => typeof value === 'number' || value instanceof JavaDouble;

const numberOf = value => (value instanceof JavaDouble ? value.value : value);

const isMap = value =>
  value !== null && typeof value === 'object' && !Array.isArray(value) && !(value instanceof JavaDouble);

/** Strips the trailing options object Handlebars appends to every helper's arguments. */
const paramsOf = args => Array.prototype.slice.call(args, 0, args.length - 1);
const optionsOf = args => args[args.length - 1];
const hashOf = args => (optionsOf(args) && optionsOf(args).hash) || {};

/**
 * Formats a Date with a java.time.DateTimeFormatter pattern - the server builds its formatter from
 * the same pattern string, so 'MMMM yyyy' etc. must mean the same thing here.
 */
const formatJavaPattern = (date, pattern, locale, timeZone) => {
  const loc = toLocale(locale);
  const zone = timeZone || undefined;

  const numeric = new Intl.DateTimeFormat('en-GB', {
    timeZone: zone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  }).formatToParts(date);

  const part = type => (numeric.find(p => p.type === type) || {}).value || '';
  const named = opts => new Intl.DateTimeFormat(loc, { timeZone: zone, ...opts }).format(date);

  const year = part('year');
  // Intl can report hour 24 for midnight; java.time never does.
  const hour24 = part('hour') === '24' ? '00' : part('hour');
  const hour12 = String(Number(hour24) % 12 === 0 ? 12 : Number(hour24) % 12).padStart(2, '0');

  const tokens = {
    yyyy: year,
    YYYY: year,
    yy: year.slice(-2),
    MMMM: named({ month: 'long' }),
    MMM: named({ month: 'short' }),
    MM: part('month'),
    M: String(Number(part('month'))),
    dd: part('day'),
    d: String(Number(part('day'))),
    EEEE: named({ weekday: 'long' }),
    EEE: named({ weekday: 'short' }),
    HH: hour24,
    H: String(Number(hour24)),
    hh: hour12,
    h: String(Number(hour12)),
    mm: part('minute'),
    m: String(Number(part('minute'))),
    ss: part('second'),
    s: String(Number(part('second'))),
    a: Number(hour24) < 12 ? 'AM' : 'PM',
  };

  // Longest tokens first so MMMM wins over MM. Single-quoted runs are literals, as in java.time.
  const tokenPattern = /'([^']*)'|yyyy|YYYY|yy|MMMM|MMM|MM|M|dd|d|EEEE|EEE|HH|H|hh|h|mm|m|ss|s|a/g;
  return String(pattern).replace(tokenPattern, (match, literal) => (literal !== undefined ? literal : tokens[match]));
};

/** Walks a dotted path through nested objects, as ListSumHelper does. */
const valueAtPath = (item, path) =>
  String(path)
    .split('.')
    .reduce((current, key) => (isMap(current) ? current[key] : undefined), item);

/**
 * Handlebars.Utils.isEmpty - note that the number 0 counts as empty, which is what makes every
 * StringHelpers helper fall through to its fallback param when handed a zero.
 */
const isEmpty = value => {
  if (value === null || value === undefined) return true;
  if (typeof value === 'string') return value.length === 0;
  if (Array.isArray(value)) return value.length === 0;
  if (typeof value === 'boolean') return !value;
  if (isNumeric(value)) return numberOf(value) === 0;
  return false;
};

/**
 * The shared StringHelpers.apply: an empty context yields the first param (a fallback), or nothing.
 * Only the helpers that override apply in Java skip this.
 */
const stringHelper = safeApply =>
  function () {
    const params = paramsOf(arguments);
    const context = params[0];
    const hash = hashOf(arguments);
    if (isEmpty(context)) {
      return params[1] === undefined ? null : String(params[1]);
    }
    return safeApply(context, params.slice(1), hash, optionsOf(arguments));
  };

// --- org.apache.commons StringUtils / WordUtils behaviour the helpers lean on ---

const repeatToLength = (pad, length) => {
  if (!pad) return ' '.repeat(Math.max(0, length));
  let out = '';
  while (out.length < length) out += pad;
  return out.slice(0, length);
};

const rightPad = (str, size, pad) => (str.length >= size ? str : str + repeatToLength(pad, size - str.length));

const leftPad = (str, size, pad) => (str.length >= size ? str : repeatToLength(pad, size - str.length) + str);

const center = (str, size, pad) => {
  const pads = size - str.length;
  if (pads <= 0) return str;
  return rightPad(leftPad(str, str.length + Math.floor(pads / 2), pad), size, pad);
};

/** WordUtils.capitalize: first letter of each whitespace-delimited word, rest untouched. */
const capitalizeWords = (str, fully) => {
  const base = fully ? str.toLowerCase() : str;
  return base.replace(/(^|\s)(\S)/g, (m, space, char) => space + char.toUpperCase());
};

/** WordUtils.wrap with the default wrapLongWords=false: break on spaces, never mid-word. */
const wordWrap = (str, length) => {
  const lines = [];
  let remaining = str;
  while (remaining.length > length) {
    const window = remaining.slice(0, length + 1);
    let breakAt = window.lastIndexOf(' ');
    if (breakAt < 0) {
      // No space inside the window: keep the long word whole and break after it.
      const nextSpace = remaining.indexOf(' ', length);
      if (nextSpace < 0) break;
      breakAt = nextSpace;
    }
    lines.push(remaining.slice(0, breakAt));
    remaining = remaining.slice(breakAt + 1);
  }
  lines.push(remaining);
  return lines.join('\n');
};

/** StringUtils.abbreviate, including its refusal to work below width 4. */
const abbreviate = (str, maxWidth) => {
  if (str.length <= maxWidth) return str;
  if (maxWidth < 4) throw new Error('Minimum abbreviation width is 4');
  return `${str.slice(0, maxWidth - 3)}...`;
};

// --- java.text.NumberFormat / DecimalFormat, as far as Intl can reasonably go ---

/** NumberFormat.getCurrencyInstance takes its currency from the locale; Intl needs it spelled out. */
const CURRENCY_BY_REGION = {
  GB: 'GBP',
  US: 'USD',
  IE: 'EUR',
  DE: 'EUR',
  FR: 'EUR',
  ES: 'EUR',
  IT: 'EUR',
  NL: 'EUR',
  BE: 'EUR',
  PT: 'EUR',
  AT: 'EUR',
  CH: 'CHF',
  AU: 'AUD',
  NZ: 'NZD',
  CA: 'CAD',
  JP: 'JPY',
  IN: 'INR',
  ZA: 'ZAR',
  SE: 'SEK',
  NO: 'NOK',
  DK: 'DKK',
  PL: 'PLN',
};

const currencyForLocale = locale => {
  const region = String(locale).split('-')[1];
  return (region && CURRENCY_BY_REGION[region.toUpperCase()]) || 'GBP';
};

/**
 * A DecimalFormat pattern, reduced to the Intl options it maps onto: grouping, minimum/maximum
 * fraction digits, minimum integer digits and percent. Anything more exotic in the pattern is
 * ignored rather than guessed at.
 */
const decimalPatternOptions = pattern => {
  const percent = pattern.includes('%');
  const body = pattern.replace(/[%¤'"]/g, '');
  const [integerPart = '', fractionPart = ''] = body.split('.');
  const zeros = part => (part.match(/0/g) || []).length;

  return {
    ...JAVA_ROUNDING,
    style: percent ? 'percent' : 'decimal',
    useGrouping: integerPart.includes(','),
    minimumIntegerDigits: Math.max(1, zeros(integerPart)),
    minimumFractionDigits: zeros(fractionPart),
    maximumFractionDigits: Math.max(zeros(fractionPart), (fractionPart.match(/[0#]/g) || []).length),
  };
};

/**
 * Every java.text.DecimalFormat defaults to RoundingMode.HALF_EVEN, while Intl defaults to
 * halfExpand - so 1234.5 formats as "1,234" on the server and would be "1,235" here.
 *
 * Intl grew a roundingMode option, but it is ignored by older engines (Node 16 among them), so the
 * value is rounded explicitly before formatting rather than trusting the option. The option is
 * still passed through for engines that honour it, which also covers the non-HALF_EVEN modes.
 */
const JAVA_ROUNDING = { roundingMode: 'halfEven' };

/** java.math.RoundingMode, applied at a given number of decimal places. */
const roundJava = (value, digits, mode) => {
  const factor = Math.pow(10, digits);
  const scaled = value * factor;
  const floor = Math.floor(scaled);
  const isTie = Math.abs(scaled - floor - 0.5) < 1e-9;
  const alreadyExact = Math.abs(scaled - Math.round(scaled)) < 1e-9;

  let rounded;
  switch (mode) {
    case 'UP':
      rounded = scaled < 0 ? Math.floor(scaled) : Math.ceil(scaled);
      break;
    case 'DOWN':
      rounded = Math.trunc(scaled);
      break;
    case 'CEILING':
      rounded = Math.ceil(scaled);
      break;
    case 'FLOOR':
      rounded = Math.floor(scaled);
      break;
    case 'HALF_DOWN':
      rounded = isTie ? Math.trunc(scaled) : Math.round(Math.abs(scaled)) * Math.sign(scaled);
      break;
    case 'HALF_UP':
      rounded = Math.round(Math.abs(scaled)) * (scaled < 0 ? -1 : 1);
      break;
    case 'UNNECESSARY':
      if (!alreadyExact) throw new Error('Rounding necessary');
      rounded = Math.round(scaled);
      break;
    case 'HALF_EVEN':
    default:
      rounded = isTie ? (floor % 2 === 0 ? floor : floor + 1) : Math.round(scaled);
      break;
  }
  return rounded / factor;
};

/**
 * Formats through Intl, but pre-rounds the way the JVM would first. Percent styles scale by 100
 * before rounding, matching NumberFormat.getPercentInstance.
 */
const formatWithJavaRounding = (locale, options, value, roundingMode = 'HALF_EVEN') => {
  const formatter = new Intl.NumberFormat(locale, options);
  const resolved = formatter.resolvedOptions();
  if (resolved.maximumFractionDigits === undefined) {
    return formatter.format(value);
  }
  const scale = resolved.style === 'percent' ? 100 : 1;
  return formatter.format(roundJava(value * scale, resolved.maximumFractionDigits, roundingMode) / scale);
};

const NUMBER_STYLES = {
  DEFAULT: () => ({ ...JAVA_ROUNDING, style: 'decimal', maximumFractionDigits: 3 }),
  INTEGER: () => ({ ...JAVA_ROUNDING, style: 'decimal', maximumFractionDigits: 0 }),
  CURRENCY: locale => ({ ...JAVA_ROUNDING, style: 'currency', currency: currencyForLocale(locale) }),
  PERCENT: () => ({ ...JAVA_ROUNDING, style: 'percent', maximumFractionDigits: 0 }),
};

const HASH_TO_INTL = {
  groupingUsed: 'useGrouping',
  maximumFractionDigits: 'maximumFractionDigits',
  minimumFractionDigits: 'minimumFractionDigits',
  minimumIntegerDigits: 'minimumIntegerDigits',
};

/** java.util.Formatter, limited to the conversions that show up in templates. */
const stringFormat = (format, params) => {
  let index = 0;
  return format.replace(
    /%(\d+\$)?([-+ 0,#]*)(\d+)?(?:\.(\d+))?([sdfxXeE%])/g,
    (match, argIndex, flags, width, precision, conversion) => {
      if (conversion === '%') return '%';
      const value = params[argIndex ? parseInt(argIndex, 10) - 1 : index++];
      let out;
      if (conversion === 's') {
        out = value === null || value === undefined ? 'null' : String(value);
      } else if (conversion === 'd') {
        out = String(Math.trunc(Number(value) || 0));
        if (flags.includes(',')) out = Number(out).toLocaleString('en-GB');
      } else if (conversion === 'f') {
        out = Number(value || 0).toFixed(precision === undefined ? 6 : parseInt(precision, 10));
      } else if (conversion === 'x' || conversion === 'X') {
        out = Math.trunc(Number(value) || 0).toString(16);
        if (conversion === 'X') out = out.toUpperCase();
      } else {
        out = Number(value || 0).toExponential(precision === undefined ? 6 : parseInt(precision, 10));
        if (conversion === 'E') out = out.toUpperCase();
      }
      if (width) {
        const size = parseInt(width, 10);
        out = flags.includes('-')
          ? rightPad(out, size, ' ')
          : flags.includes('0')
          ? leftPad(out, size, '0')
          : leftPad(out, size, ' ');
      }
      return out;
    },
  );
};

/**
 * dateFormat.toTemporalAccessor accepts only a TemporalAccessor or a Date. A JSON payload can carry
 * neither, so {{dateFormat someEpoch}} throws on the server - and must throw here, or the preview
 * would quietly render a date the PDF will never produce. Use the zervio formatDate helper for
 * epoch seconds.
 */
const toDate = value => {
  if (value instanceof Date) return value;
  throw new Error(
    `found instance of ${value === null || value === undefined ? null : typeof value} with value ` +
      `'${value}', but expected instance of TemporalAccessor`,
  );
};

const LOCALIZED_DATE_STYLES = { full: 'full', long: 'long', medium: 'medium', short: 'short' };

const formatDateHelper = (value, params, hash) => {
  const date = toDate(value);
  const pattern = params[0] === undefined ? hash.format || 'medium' : params[0];
  const locale = toLocale(params[1] || hash.locale);
  const dateStyle = LOCALIZED_DATE_STYLES[pattern];

  if (!dateStyle) {
    return formatJavaPattern(date, pattern, locale, hash.tz);
  }
  const timeStyle = LOCALIZED_DATE_STYLES[hash.time];
  return new Intl.DateTimeFormat(locale, {
    timeZone: hash.tz || undefined,
    dateStyle,
    ...(timeStyle ? { timeStyle } : {}),
  }).format(date);
};

const STRING_HELPERS = {
  capitalizeFirst: stringHelper(value => {
    const text = String(value);
    return text.charAt(0).toUpperCase() + text.slice(1);
  }),

  center: stringHelper((value, params, hash) => {
    if (hash.size === null || hash.size === undefined) throw new Error("found 'null', expected 'size'");
    return center(String(value), hash.size, hash.pad === undefined ? ' ' : hash.pad);
  }),

  // Overrides apply in Java: an empty context yields '', not a fallback param.
  cut() {
    const params = paramsOf(arguments);
    if (isEmpty(params[0])) return '';
    const strip = params[1] === undefined ? ' ' : String(params[1]);
    return String(params[0]).split(strip).join('');
  },

  defaultIfEmpty() {
    const params = paramsOf(arguments);
    if (isEmpty(params[0])) return params[1] === undefined ? '' : params[1];
    return String(params[0]);
  },

  /** The separator is the LAST param, not the second - {{join a b c ", "}} is valid. */
  join() {
    const params = paramsOf(arguments);
    const hash = hashOf(arguments);
    const context = params[0];
    if (isEmpty(context)) return '';

    const rest = params.slice(1);
    const separator = rest.length ? rest[rest.length - 1] : null;
    if (separator === null || separator === undefined) {
      throw new Error(`found 'null', expected 'separator' at param[${rest.length}]`);
    }
    if (typeof separator !== 'string') {
      throw new Error(`found '${separator}', expected 'separator' at param[${rest.length}]`);
    }
    const prefix = hash.prefix === undefined ? '' : hash.prefix;
    const suffix = hash.suffix === undefined ? '' : hash.suffix;
    const values = Array.isArray(context) ? context : [context, ...rest.slice(0, -1)];
    return prefix + values.join(separator) + suffix;
  },

  ljust: stringHelper((value, params, hash) => {
    if (hash.size === null || hash.size === undefined) throw new Error("found 'null', expected 'size'");
    return rightPad(String(value), hash.size, hash.pad === undefined ? ' ' : hash.pad);
  }),

  rjust: stringHelper((value, params, hash) => {
    if (hash.size === null || hash.size === undefined) throw new Error("found 'null', expected 'size'");
    return leftPad(String(value), hash.size, hash.pad === undefined ? ' ' : hash.pad);
  }),

  substring: stringHelper((value, params) => {
    if (params[0] === undefined) throw new Error('Required start offset: ');
    const text = String(value);
    const end = params[1] === undefined ? text.length : params[1];
    return text.substring(params[0], end);
  }),

  lower: stringHelper(value => String(value).toLowerCase()),

  upper: stringHelper(value => String(value).toUpperCase()),

  /** Letters only, lowercased; whitespace becomes '-'. Digits and punctuation are dropped. */
  slugify: stringHelper(value => {
    const text = String(value).trim();
    let out = '';
    for (const char of text) {
      if (/\p{L}/u.test(char)) out += char.toLowerCase();
      else if (/\s/.test(char)) out += '-';
    }
    return out;
  }),

  stringFormat: stringHelper((value, params) => stringFormat(String(value), params)),

  stripTags: stringHelper(value => String(value).replace(/<[^>]*>/g, '')),

  capitalize: stringHelper((value, params, hash) => capitalizeWords(String(value), Boolean(hash.fully))),

  abbreviate: stringHelper((value, params) => {
    if (params[0] === null || params[0] === undefined) throw new Error("found 'null', expected 'width'");
    return abbreviate(String(value), params[0]);
  }),

  wordWrap: stringHelper((value, params) => {
    if (params[0] === null || params[0] === undefined) throw new Error("found 'null', expected 'length'");
    return wordWrap(String(value), params[0]);
  }),

  replace: stringHelper((value, params) =>
    String(value)
      .split(String(params[0]))
      .join(String(params[1] === undefined ? '' : params[1])),
  ),

  /** Booleans only, and the labels come from the hash - {{yesno flag yes="Y" no="N"}}. */
  yesno() {
    const params = paramsOf(arguments);
    const hash = hashOf(arguments);
    const value = params[0];
    if (value === null || value === undefined) return hash.maybe === undefined ? 'maybe' : hash.maybe;
    if (typeof value !== 'boolean') throw new Error(`found '${value}', expected 'boolean'`);
    if (value === true) return hash.yes === undefined ? 'yes' : hash.yes;
    return hash.no === undefined ? 'no' : hash.no;
  },

  dateFormat: stringHelper(formatDateHelper),

  /**
   * Overrides apply in Java: a Number always formats, so zero is not treated as empty here.
   * A non-number returns the fallback param instead.
   */
  numberFormat() {
    const params = paramsOf(arguments);
    const hash = hashOf(arguments);
    const value = params[0];

    if (!isNumeric(value)) {
      return params[1] === undefined ? null : String(params[1]);
    }

    const rawFormat = params[1] === undefined ? hash.format : params[1];
    const format = !rawFormat || !String(rawFormat).trim() ? 'DEFAULT' : String(rawFormat).toUpperCase().trim();
    const locale = toLocale(params[2] || hash.locale);

    const options = NUMBER_STYLES[format] ? NUMBER_STYLES[format](locale) : decimalPatternOptions(String(rawFormat));

    Object.entries(HASH_TO_INTL).forEach(([hashKey, intlKey]) => {
      if (hash[hashKey] !== undefined && hash[hashKey] !== null) options[intlKey] = hash[hashKey];
    });

    const mode = hash.roundingMode ? String(hash.roundingMode).toUpperCase().trim() : 'HALF_EVEN';
    return formatWithJavaRounding(locale, options, numberOf(value), mode);
  },

  now() {
    return formatDateHelper(new Date(), paramsOf(arguments).slice(1), hashOf(arguments));
  },
};

const ZERVIO_HELPERS = {
  /**
   * Spread first, so a zervio helper sharing a name overrides it - the same precedence as
   * TemplatingService, which calls StringHelpers.register before registering its own.
   */
  ...STRING_HELPERS,

  // --- FormatDateHelper: the value is a unix timestamp in SECONDS ---
  formatDate(value) {
    if (value === null || value === undefined || value === '') return '';
    const params = paramsOf(arguments).slice(1);
    const hash = hashOf(arguments);
    const seconds = Number(value);
    if (Number.isNaN(seconds)) return '';
    return formatJavaPattern(new Date(seconds * 1000), params[0] || DEFAULT_PATTERN, params[1] || hash.locale, hash.tz);
  },

  // --- FormatLocalDateHelper: the value is an ISO local date, e.g. 2026-08-28 ---
  formatLocalDate(text) {
    if (text === null || text === undefined || text === '') return '';
    const params = paramsOf(arguments).slice(1);
    const hash = hashOf(arguments);
    const date = new Date(`${text}T00:00:00Z`);
    if (Number.isNaN(date.getTime())) return '';
    return formatJavaPattern(date, params[0] || DEFAULT_PATTERN, params[1] || hash.locale, hash.tz || 'UTC');
  },

  // --- FormatSufixDateHelper: "1st of January 2026", from unix seconds ---
  formatDateWithSuffix(value) {
    if (value === null || value === undefined || value === '') return '';
    const seconds = Number(value);
    if (Number.isNaN(seconds)) return '';
    const date = new Date(seconds * 1000);
    const day = Number(formatJavaPattern(date, 'd'));
    const suffix = day >= 11 && day <= 13 ? 'th' : { 1: 'st', 2: 'nd', 3: 'rd' }[day % 10] || 'th';
    return `${day}${suffix} of ${formatJavaPattern(date, 'MMMM yyyy')}`;
  },

  // --- CurrentDateHelper: {{currentDate "yyyy-MM-dd" 5 "DAY"}} ---
  currentDate(format) {
    const params = paramsOf(arguments).slice(1);
    const hash = hashOf(arguments);
    const amount = toNumber(params[0]);
    const unit = String(params[1] || 'DAY').toUpperCase();

    const date = new Date();
    if (unit === 'YEAR') date.setFullYear(date.getFullYear() + amount);
    else if (unit === 'MONTH') date.setMonth(date.getMonth() + amount);
    else if (unit === 'DAY') date.setDate(date.getDate() + amount);
    else throw new Error(`Unsupported unit: ${unit}`);

    return formatJavaPattern(date, typeof format === 'string' ? format : DEFAULT_PATTERN, null, hash.tz);
  },

  // --- ListSizeHelper (also tolerates objects, which the old editor page relied on) ---
  /**
   * ListSizeHelper is Helper<Collection>, so handlebars casts the context to a Collection: a map or
   * a string blows up with a ClassCastException on the server rather than returning a length.
   */
  size(collection) {
    if (collection === null || collection === undefined) return 0;
    if (Array.isArray(collection)) return collection.length;
    throw new Error(`class ${typeof collection} cannot be cast to class java.util.Collection`);
  },

  // --- LastHelper: returns 0 for a missing collection, matching the server ---
  last(collection) {
    if (!Array.isArray(collection) || collection.length === 0) return 0;
    return collection[collection.length - 1];
  },

  // --- SumListHelper: {{sumList items "amount"}} ---
  sumList(collection, field) {
    if (!Array.isArray(collection) || !field || typeof field !== 'string') return javaDouble(0.0);
    return javaDouble(
      collection.reduce(
        (total, item) => (isMap(item) && typeof item[field] === 'number' ? total + item[field] : total),
        0.0,
      ),
    );
  },

  // --- ListSumHelper: {{listSum items "price.net"}} ---
  listSum(collection, fieldPath) {
    if (!Array.isArray(collection) || collection.length === 0) return javaDouble(0.0);
    if (!fieldPath || typeof fieldPath !== 'string') return javaDouble(0.0);
    return javaDouble(
      collection.reduce((total, item) => {
        const value = valueAtPath(item, fieldPath);
        return typeof value === 'number' ? total + value : total;
      }, 0.0),
    );
  },

  // --- JoinListHelper: {{joinList items "name" ", "}} ---
  joinList(collection, field, delimiter) {
    if (!Array.isArray(collection)) return javaDouble(0.0);
    if (!field || typeof field !== 'string') return '';
    return collection
      .filter(item => isMap(item) && item[field] !== null && item[field] !== undefined)
      .map(item => item[field])
      .join(delimiter === undefined || delimiter === null ? '' : delimiter);
  },

  // --- FilterListHelper: {{#each (filterList items "type" "FEE")}} ---
  filterList(collection, fieldName, matchValue) {
    if (!Array.isArray(collection)) return [];
    if (fieldName === undefined || fieldName === null || matchValue === undefined || matchValue === null) {
      return collection;
    }
    return collection.filter(item => isMap(item) && item[fieldName] === matchValue);
  },

  // --- AtHelper: {{at items 0}} ---
  at(collection, index) {
    if (!Array.isArray(collection)) return null;
    const i = index === undefined ? 0 : toNumber(index);
    return i >= 0 && i < collection.length ? collection[i] : null;
  },

  // --- FormatCurrencyHelper: {{formatCurrency total "GBP" locale="en_GB"}} ---
  formatCurrency(value) {
    const params = paramsOf(arguments).slice(1);
    const hash = hashOf(arguments);
    const options = {
      style: 'currency',
      currency: params[0] ? String(params[0]) : 'GBP',
    };
    if (typeof hash.minimumFractionDigits === 'number') {
      options.minimumFractionDigits = hash.minimumFractionDigits;
    }
    if (typeof hash.maximumFractionDigits === 'number') {
      options.maximumFractionDigits = hash.maximumFractionDigits;
    }
    return formatWithJavaRounding(toLocale(hash.locale), options, toNumber(value));
  },

  // --- PercentHelper: {{percent total 20}} -> 20% of total ---
  percent(total, part) {
    return javaDouble((toNumber(part) / 100) * toNumber(total));
  },

  // --- ConcatHelper ---
  concat(first) {
    if (first === null || first === undefined) return null;
    return paramsOf(arguments)
      .filter(param => param !== null && param !== undefined)
      .join('');
  },

  // --- ColumnHelper: encodes a table column spec the PDF renderer parses ---
  column(name) {
    const params = paramsOf(arguments).slice(1);
    if (name === null || name === undefined || params.length === 0) return '';
    const size = params[0] === undefined ? 100 : params[0];
    const align = params[1] === undefined ? 'LEFT' : params[1];
    const span = params[2] === undefined ? 1 : params[2];
    const style = params[3] === undefined ? 'NONE' : params[3];
    return `${name}::${size}::${align}::${span}::${style}`;
  },

  // --- AddValueHelper: non-numeric arguments count as 0 ---
  addValues() {
    return javaDouble(paramsOf(arguments).reduce((sum, param) => sum + toNumber(param), 0.0));
  },

  // --- NumberHelper.add/multiply/divide/subtract ---
  add(context) {
    return javaDouble(
      paramsOf(arguments)
        .slice(1)
        .reduce((total, param) => total + toNumber(param), toNumber(context)),
    );
  },
  multiply(context) {
    return javaDouble(
      paramsOf(arguments)
        .slice(1)
        .reduce((total, param) => total * toNumber(param), toNumber(context)),
    );
  },
  divide(context) {
    return javaDouble(
      paramsOf(arguments)
        .slice(1)
        .reduce((total, param) => total / toNumber(param), toNumber(context)),
    );
  },
  subtract(context) {
    return javaDouble(
      paramsOf(arguments)
        .slice(1)
        .reduce((total, param) => total - toNumber(param), toNumber(context)),
    );
  },

  // --- inline lambdas registered in TemplatingService ---
  notInList(value, listString) {
    const list = String(listString === undefined ? '' : listString).split(',');
    return !list.includes(String(value));
  },
  notAnyInList(value, listString) {
    if (!Array.isArray(value)) throw new Error('value must be a list');
    const list = String(listString === undefined ? '' : listString).split(',');
    return !value.some(item => list.includes(item));
  },
  lexiconsNotExists(value, listString) {
    if (!isMap(value)) return true;
    const forbidden = String(listString === undefined ? '' : listString).split(',');
    return !Object.keys(value).some(key => forbidden.includes(key));
  },
  positive(value) {
    return javaDouble(Math.abs(toNumber(value)));
  },

  // --- jknack ConditionalHelpers, ported from handlebars 4.5.0 ---
  ...(() => {
    /** ConditionalHelpers.eq: Objects.equals, falling back to a numeric compare (2 vs 2.0). */
    const eq = (a, b) => {
      if (a === b) return true;
      if (isNumeric(a) && isNumeric(b)) return numberOf(a) === numberOf(b);
      return false;
    };

    /** ConditionalHelpers.cmp: compareTo, falling back to Double.compare for mixed number types. */
    const cmp = (a, b) => {
      if (isNumeric(a) && isNumeric(b)) {
        const left = numberOf(a);
        const right = numberOf(b);
        return left < right ? -1 : left > right ? 1 : 0;
      }
      const left = String(a);
      const right = String(b);
      return left < right ? -1 : left > right ? 1 : 0;
    };

    /**
     * As a section the helper renders a branch; inline it yields the yes/no hash, defaulting to
     * true/false - not the raw boolean, which is what the previous port returned.
     */
    const conditional = test =>
      function () {
        const params = paramsOf(arguments);
        const options = optionsOf(arguments);
        const hash = hashOf(arguments);
        const result = test(params);
        if (options && typeof options.fn === 'function') {
          return result ? options.fn(this) : options.inverse(this);
        }
        if (result) return hash.yes === undefined ? true : hash.yes;
        return hash.no === undefined ? false : hash.no;
      };

    return {
      eq: conditional(params => eq(params[0], params[1])),
      neq: conditional(params => !eq(params[0], params[1])),
      gt: conditional(params => cmp(params[0], params[1]) > 0),
      gte: conditional(params => cmp(params[0], params[1]) >= 0),
      lt: conditional(params => cmp(params[0], params[1]) < 0),
      lte: conditional(params => cmp(params[0], params[1]) <= 0),
      // and/or/not test Utils.isEmpty, not JS truthiness - an empty array is falsy to the JVM.
      and: conditional(params => params.every(param => !isEmpty(param))),
      or: conditional(params => params.some(param => !isEmpty(param))),
      not: conditional(params => isEmpty(params[0])),
    };
  })(),
};

export const ZERVIO_HELPER_NAMES = Object.keys(ZERVIO_HELPERS).sort();

/**
 * Registers every ported helper on a Handlebars environment. Uses an isolated environment rather
 * than the global one so the rest of the dashboard is unaffected.
 */
export const createTemplatingEnvironment = () => {
  const environment = Handlebars.create();
  Object.entries(ZERVIO_HELPERS).forEach(([name, helper]) => environment.registerHelper(name, helper));
  return environment;
};

const sharedEnvironment = createTemplatingEnvironment();

/**
 * Renders a template the way HtmlGenerator does. Returns the rendered html, or the error so the
 * editor can show it instead of a blank preview.
 *
 * Mirrors TemplatingService.apply: a body with no '{{' is returned untouched.
 */
export const renderTemplate = (templateData, event) => {
  if (!templateData) return { html: '', error: null };
  if (!templateData.includes('{{')) return { html: templateData, error: null };

  try {
    // ignoreStandalone keeps the whitespace around a block tag that sits alone on its line.
    // handlebars.js strips it by default; handlebars.java does not, and the pdf renderer is handed
    // the java output - so stripping here would make the preview's html diverge from the document's.
    const render = sharedEnvironment.compile(templateData, { ignoreStandalone: true });
    return { html: render(event || {}), error: null };
  } catch (e) {
    return { html: null, error: e.message || String(e) };
  }
};

/**
 * The generator exposes a template version's images as static_images.<lexicon>.url. The editor has
 * no media resolution, so this keeps previews from breaking on those lexicons.
 */
export const withStaticImages = (event, images) => {
  if (!images || images.length === 0) return event;
  const staticImages = images.reduce((acc, image) => ({ ...acc, [image.lexicon]: { url: image.url || '' } }), {});
  return { ...(event || {}), static_images: staticImages };
};
